"use client";

import Image from "next/image";
import Link from "next/link";
import type { ComponentType, ReactNode } from "react";
import { useCallback } from "react";
import { useContext } from "react";
import {
  LuBadgeDollarSign,
  LuBell,
  LuCircleHelp as LuHelpCircle,
  LuDollarSign,
  LuHouse as LuHome,
  LuLandmark,
  LuLayers,
  LuLock,
  LuMail,
  LuMap,
  LuPencil,
  LuRss,
  LuScrollText,
  LuSettings,
  LuShare,
  LuUser as LuUser2,
  LuWallet,
} from "react-icons/lu";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { focusIOSKeyboardBridge } from "@/components/app/editor-v3/editor-dom-focus";
import { IsLoggedIn, useIsLoggedIn } from "@/components/shared/is-logged-in";
import {
  MenuAttentionIndicator,
  type MenuAttentionKey,
} from "@/components/shared/menu-attention-indicator";
import { SignUp } from "@/components/shared/signup";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  UserContext,
  useUnreadMessagesSignalState,
  useUnreadNotificationsSignalState,
  useUserAccountSignalState,
  useUserAuthSignalState,
} from "@/contexts/user-context";
import { useBackgroundPathname } from "@/hooks/use-background-pathname";
import {
  APP_NAME_GENERIC,
  APP_ROUTES,
  DESO_APP_NAME,
  DESO_WEBSITE,
  WHITEPAPER_DOC_URL,
} from "@/utils/constants";
import { centsToUSD } from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { matchRoute } from "@/utils/route-utils";

import { AppLayoutUserMenu } from "./app-layout-user-menu";
import SidebarFeedbackWidget from "./sidebar-top-resutls-widget/sidebar-feedback-widget";

interface NavItem {
  href: string;
  label: string;
  Icon: ComponentType<{ className: string }>;
  badge?: string;
  attentionKey?: MenuAttentionKey;
  hasTopMargin?: boolean;
  rootPath?: boolean;
  amountUsdCents?: number;
  hidden?: boolean;
}

type DesktopNavigationProps = {
  children?: ReactNode;
  closeDrawer?: () => void;
  dismissDrawer?: () => void;
  preparePostDialogOpen?: () => void;
  replaceLinks?: boolean;
  setDrawerOpen?: (open: boolean) => void;
};

export const DesktopNavigation = ({
  children,
  closeDrawer,
  dismissDrawer,
  preparePostDialogOpen,
  replaceLinks = false,
  setDrawerOpen,
}: DesktopNavigationProps) => {
  const pathname = useBackgroundPathname();
  const { isAdmin } = useContext(UserContext);
  const { user } = useUserAccountSignalState();
  const { currentUserPublicKey } = useUserAuthSignalState();
  const { unreadNotifications } = useUnreadNotificationsSignalState();
  const { totalUnclaimedMessageTipsUsdCents, unreadTotalMessagesCount } =
    useUnreadMessagesSignalState();
  const isLoggedIn = useIsLoggedIn();

  const navItems: NavItem[] = [
    {
      href: APP_ROUTES.app,
      label: "Home",
      Icon: LuHome,
      hasTopMargin: false,
    },
    {
      href: APP_ROUTES.discover,
      label: "Discover",
      Icon: LuMap,
      hasTopMargin: false,
    },
    {
      href: APP_ROUTES.bounty,
      label: "Bounties",
      Icon: LuBadgeDollarSign,
      hasTopMargin: false,
      hidden: true,
    },
    {
      href: `${APP_ROUTES.collections}/${user?.username || currentUserPublicKey}`,
      label: "Collections",
      Icon: LuLayers,
      hasTopMargin: false,
      hidden: !isLoggedIn,
    },
    {
      href: APP_ROUTES.feedStore,
      label: "Feed Store",
      Icon: LuRss,
      hasTopMargin: true,
      hidden: true,
    },
    {
      href: APP_ROUTES.trade,
      label: "Trade",
      Icon: LuDollarSign,
      hasTopMargin: false,
    },
    {
      href: APP_ROUTES.notifications,
      label: "Notifications",
      Icon: LuBell,
      badge: unreadNotifications
        ? abbreviateNumber(unreadNotifications)
        : undefined,
      hasTopMargin: true,
      hidden: !isLoggedIn,
    },
    {
      href: APP_ROUTES.messages,
      label: "Messages",
      Icon: LuMail,
      badge:
        Number(unreadTotalMessagesCount) > 0
          ? abbreviateNumber(Number(unreadTotalMessagesCount))
          : undefined,
      amountUsdCents: totalUnclaimedMessageTipsUsdCents,
      hasTopMargin: false,
      hidden: !isLoggedIn,
    },
    {
      href: APP_ROUTES.earnings,
      label: "Earnings",
      Icon: LuLandmark,
      hasTopMargin: true,
    },
    {
      href: APP_ROUTES.wallet,
      label: "Wallet",
      Icon: LuWallet,
      hasTopMargin: false,
    },
    {
      href: user ? `/${user?.username ?? user?.publicKey}` : "#",
      label: "Profile",
      Icon: LuUser2,
      hasTopMargin: true,
      hidden: !isLoggedIn,
    },
    {
      href: APP_ROUTES.settings,
      label: "Settings",
      Icon: LuSettings,
      attentionKey: "subscription-tier-health" as const,
      hasTopMargin: false,
      rootPath: true,
      hidden: !isLoggedIn,
    },
  ].filter((item) => !item.hidden);

  const adminNavItems: Array<NavItem> = [
    {
      href: APP_ROUTES.admin,
      label: "Admin",
      Icon: LuLock,
      hasTopMargin: false,
      rootPath: true,
    },
  ];

  const activeClass =
    "pl-4 pr-2 py-2 w-full rounded-full bg-card hover:bg-card hover:text-muted-foreground cursor-pointer flex text-base items-center gap-4";
  const inActiveClass =
    "pl-4 pr-2 py-2 w-full rounded-full hover:bg-card hover:text-muted-foreground cursor-pointer flex text-base items-center gap-4";

  const isRootSourcePath = (path: string) => {
    const allNavItems = [...navItems, ...adminNavItems];
    return allNavItems.some(
      (item) =>
        item.rootPath && path === item.href && pathname.startsWith(item.href),
    );
  };

  const isActive = useCallback(
    (path: string): boolean => {
      if (isRootSourcePath(path)) {
        return true;
      }

      if (path === "/" && pathname !== "/") {
        return false;
      }

      return matchRoute(pathname, path);
    },
    [pathname, isRootSourcePath],
  );

  const menuItems = isAdmin ? [...navItems, ...adminNavItems] : [...navItems];

  return (
    <div className="flex flex-col gap-4">
      {children}
      <div
        className="
          -mx-4 mb-4
          lg:mx-0
        "
      >
        <ul className="flex flex-col gap-[2px]">
          {menuItems.map(
            ({
              href,
              label,
              Icon,
              badge,
              attentionKey,
              hasTopMargin,
              amountUsdCents,
            }) => (
              <Link
                href={href}
                key={label}
                onClick={(event) => {
                  if (!replaceLinks || !isActive(href)) {
                    return;
                  }

                  event.preventDefault();
                  closeDrawer?.();
                }}
                onNavigate={() => {
                  if (replaceLinks) {
                    window.setTimeout(() => dismissDrawer?.(), 0);
                    return;
                  }

                  setDrawerOpen?.(false);
                }}
                prefetch={false}
                replace={replaceLinks}
              >
                <li
                  className={cn(
                    "relative",
                    isActive(href) ? activeClass : inActiveClass,
                    hasTopMargin && "mt-4",
                  )}
                >
                  <div className="flex items-center">
                    <Icon className="text-xl text-muted" />
                    <span className="ml-2 text-muted-foreground">{label}</span>
                    <MenuAttentionIndicator
                      attentionKey={attentionKey}
                      animationKey={`desktop-nav:${href}`}
                      className="ml-2"
                    />
                  </div>
                  {badge && (
                    <Badge className="text-xs" variant="destructive">
                      {badge}
                    </Badge>
                  )}
                  {typeof amountUsdCents === "number" && amountUsdCents > 0 && (
                    <span className="relative top-px font-mono text-emerald-400">
                      $
                      {abbreviateNumber(
                        amountUsdCents < 10000
                          ? centsToUSD(amountUsdCents)
                          : // We don't want to show decimals/cents for amounts over $100
                            Math.floor(centsToUSD(amountUsdCents)),
                        {
                          // Show 2 decimal places for amounts less than $100 that have
                          // fractional cents, e.g. $0.01, $0.99, $1.01, $50.99, etc.
                          ...(amountUsdCents < 10000 &&
                            amountUsdCents % 100 > 0 && {
                              minimumFractionDigits: 2,
                            }),
                        },
                      )}
                    </span>
                  )}
                </li>
              </Link>
            ),
          )}
          <IsLoggedIn>
            <div>
              <SidebarFeedbackWidget className={cn("gap-2!", inActiveClass)}>
                <LuHelpCircle className="text-xl text-muted" />
                <span className="text-base text-muted-foreground">
                  Feedback
                </span>
              </SidebarFeedbackWidget>
            </div>
          </IsLoggedIn>
        </ul>
        {isLoggedIn ? (
          <div
            className="
              mt-8 ml-4 flex w-3/5 flex-col items-start gap-4
              md:w-[90%]
            "
          >
            <Button
              variant="default"
              className="flex w-full items-center gap-2"
              onClick={() => {
                (preparePostDialogOpen ?? focusIOSKeyboardBridge)();
                setDialog(DIALOG.AddPost, {
                  open: true,
                  replace: replaceLinks,
                });
                setDrawerOpen?.(false);
              }}
            >
              <LuPencil className="text-base" />
              Post
            </Button>
            <div className="grid w-full">
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => {
                  setDrawerOpen?.(false);
                  setDialog(DIALOG.PageShare, {
                    open: true,
                    replace: replaceLinks,
                  });
                }}
              >
                <LuShare className="text-sm text-muted" />
                Share
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="
              mx-4 mt-4 rounded-2xl border bg-accent
              lg:mx-0 lg:ml-4
            "
          >
            <SignUp
              showBountyHunterLink={false}
              innerWrapperClassName="py-4 px-2"
              secondaryLinksClassName="flex-col w-auto"
              linkClassName="min-w-[180px]"
            />
          </div>
        )}

        <IsLoggedIn>
          <div
            className="
              mx-4 mt-4 rounded-2xl border border-border-light
              hover:bg-accent
              md:w-[90%]
              lg:mx-0 lg:ml-4
            "
          >
            <AppLayoutUserMenu />
          </div>
        </IsLoggedIn>
        <div
          className="
            mx-4 mt-4 flex flex-col gap-2 rounded-2xl border border-border-light
            p-4
            md:w-[90%]
          "
        >
          <Link href={WHITEPAPER_DOC_URL} target="_blank" className="w-full">
            <div className="flex items-center gap-x-2 p-0 text-muted">
              <div className="w-4 text-center">
                <LuScrollText className="text-base text-muted" />
              </div>
              <span
                className="
                  text-xs underline-offset-2
                  hover:underline
                "
              >
                Docs for {APP_NAME_GENERIC}
              </span>
            </div>
          </Link>
          <Link href={DESO_WEBSITE} target="_blank" className="w-full">
            <div className="flex items-center gap-x-2 p-0 text-muted">
              <div className="w-4">
                <Image
                  src="/assets/logo-deso-mark.svg"
                  alt="DeSo Logo"
                  width={12}
                  height={0}
                  className="mx-auto"
                />
              </div>
              <span
                className="
                  text-xs underline-offset-2
                  hover:underline
                "
              >
                Built on {DESO_APP_NAME}
              </span>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};
